/* eslint-disable no-restricted-syntax */
import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-micro',
        'text-detail',
        'text-caption',
        'text-body-chat',
        'text-display-sm',
        'text-display-lg',
      ],
      /**
       * `rounded-object-glyph` (4px, `src/index.css:97`) must conflict with the
       * other radius utilities, or it silently loses.
       *
       * tailwind-merge only de-duplicates classes it RECOGNISES. A custom
       * utility it has never heard of is not seen as a radius at all, so
       * `cn('… rounded-full', 'rounded-object-glyph')` keeps BOTH — and which
       * one paints is decided by CSS source order, not by the call site.
       *
       * Found 2026-08-25 the only way this kind of bug is ever found: a test
       * asserted the class was absent and it wasn't. `ReachCompanyCell` swapped
       * `rounded-md` for `rounded-object-glyph` on an `Avatar` (base:
       * `rounded-full`). The old class deduped because Tailwind ships it; the
       * new one did not, so a squared 20px company mark would have rendered as
       * a circle — erasing the person-vs-organisation distinction that radius
       * exists to carry, while every class in the markup looked correct.
       */
      rounded: ['rounded-object-glyph'],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs));
}

export function cx(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs));
}

// Tremor Raw focus and interaction utilities - mapped to our app theme
export const focusInput = [
  'focus:ring-2',
  'focus:ring-ring/20',
  'focus:border-ring',
  'focus:outline-none',
  'dark:focus:ring-ring/30',
  'dark:focus:border-ring',
];

export const focusRing = [
  'outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-ring',
  'dark:focus-visible:ring-ring',
];

export const hasErrorInput = [
  'ring-2',
  'ring-destructive/20',
  'border-destructive',
  'dark:ring-destructive/30',
  'dark:border-destructive',
];

// Tremor Raw button variants - mapped to our app theme
export const buttonVariants = {
  primary: ['bg-primary', 'hover:bg-primary/90', 'text-primary-foreground', 'border-transparent'],
  secondary: [
    'bg-secondary',
    'hover:bg-secondary/80',
    'text-secondary-foreground',
    'border-border',
    'dark:bg-secondary',
    'dark:hover:bg-secondary/80',
    'dark:text-secondary-foreground',
    'dark:border-border',
  ],
  neutral: [
    'bg-neutral',
    'hover:bg-neutral-hover',
    'text-neutral-foreground',
    'border-neutral-border',
    'dark:bg-neutral',
    'dark:hover:bg-neutral-hover',
    'dark:text-neutral-foreground',
    'dark:border-neutral-border',
  ],
  light: [
    'bg-transparent',
    'hover:bg-accent',
    'text-foreground',
    'border-transparent',
    'dark:hover:bg-accent',
    'dark:text-foreground',
  ],
};

// Tremor Raw sizing utilities - mapped to our app theme
export const getSelectButtonColors = (hasError?: boolean) => {
  return hasError
    ? hasErrorInput
    : [
        'border-border',
        'bg-background',
        'text-foreground',
        'hover:bg-accent',
        'dark:border-border',
        'dark:bg-background',
        'dark:text-foreground',
        'dark:hover:bg-accent',
      ];
};

// Tremor Raw chart color utilities - mapped to our app chart colors
export const AvailableChartColorsKeys = [
  'primary',
  'emerald',
  'blue',
  'violet',
  'amber',
  'gray',
  'cyan',
  'pink',
  'lime',
  'fuchsia',
] as const;

export type AvailableChartColors = (typeof AvailableChartColorsKeys)[number];

// Map to our app's chart color system (defined in CSS)
export const chartColors = {
  primary: 'hsl(var(--chart-color-1))', // Our primary green
  emerald: 'hsl(var(--chart-color-1))', // Maps to primary
  blue: 'hsl(var(--chart-color-2))', // Complementary blue
  violet: 'hsl(var(--chart-color-5))', // Complementary purple
  amber: 'hsl(var(--chart-color-4))', // Complementary amber
  gray: 'hsl(var(--muted-foreground))', // Our muted color
  cyan: 'hsl(var(--chart-color-2))', // Maps to blue
  pink: 'hsl(var(--chart-color-3))', // Complementary magenta
  lime: 'hsl(var(--chart-color-1))', // Maps to primary
  fuchsia: 'hsl(var(--chart-color-3))', // Maps to magenta
} as const;

export const getColorClassName = (
  color: AvailableChartColors,
  type: 'bg' | 'border' | 'text' = 'bg',
) => {
  const colorMap = {
    primary: { bg: 'bg-primary', border: 'border-primary', text: 'text-primary' },
    emerald: { bg: 'bg-primary', border: 'border-primary', text: 'text-primary' },
    blue: { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-500' },
    violet: { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-500' },
    amber: { bg: 'bg-amber-500', border: 'border-amber-500', text: 'text-amber-500' },
    gray: { bg: 'bg-muted', border: 'border-muted', text: 'text-muted-foreground' },
    cyan: { bg: 'bg-cyan-500', border: 'border-cyan-500', text: 'text-cyan-500' },
    pink: { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-500' },
    lime: { bg: 'bg-primary', border: 'border-primary', text: 'text-primary' },
    fuchsia: { bg: 'bg-fuchsia-500', border: 'border-fuchsia-500', text: 'text-fuchsia-500' },
  };

  return colorMap[color][type];
};

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}
